import { Link, useSearchParams } from "react-router-dom";
import { useResource } from "hooks/useResource";
import { getNotices, getInstitutions } from "services/content";
import PageHero from "components/shared/PageHero";
import Skeleton from "components/shared/Skeleton";
import { mediaUrl } from "lib/media";

export default function NoticesPage() {
  const { data: all } = useResource(getNotices);
  const { data: institutions } = useResource(getInstitutions);
  const [params] = useSearchParams();
  const tag = params.get("tag");

  // ?tag=KCGS narrows to that institution. Untagged notices are site-wide, so
  // they stay visible — the same rule the institution pages use.
  const notices = !all ? null : tag ? all.filter((n) => !n.tags?.length || n.tags.includes(tag)) : all;

  const inst = (institutions?.items || []).find((i) => (i.noticeTag || i.short) === tag);
  const accent = inst?.color || "#005C40";

  return (
    <div className="animate-fadeIn">
      <PageHero
        title="Notice Board"
        sub={
          inst
            ? `Notices for ${inst.name}, plus notices that apply to the whole campus.`
            : "All official notices and circulars."
        }
        page="Notices"
      />

      {tag && (
        <div className="max-w-container mx-auto px-4 sm:px-10 pt-8 flex items-center gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold"
            style={{ background: `${accent}18`, color: accent }}
          >
            🏷 Showing: {inst?.name || tag}
          </span>
          <Link to="/notices" className="text-[12.5px] text-muted no-underline hover:underline">
            Show all notices
          </Link>
        </div>
      )}

      <section className="px-4 sm:px-10 py-14 max-w-container mx-auto">
        <div className="bg-white border border-line rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
          {!notices
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="px-6 py-[18px] border-b border-line">
                  <Skeleton className="h-4 w-[80%] mb-2" />
                  <Skeleton className="h-3 w-24" />
                </div>
              ))
            : notices.map((n, i) => (
                <div
                  key={i}
                  className="px-6 py-[18px] flex gap-3.5 items-start transition-colors hover:bg-[#f0f8f4]"
                  style={{ borderBottom: i < notices.length - 1 ? "1px solid #e2ddd6" : "none" }}
                >
                  <div className="w-2 h-2 rounded-full bg-green shrink-0 mt-2" />
                  <div>
                    <p className="text-[15px] leading-[1.6] text-ink">{n.text}</p>
                    <p className="text-xs text-muted mt-1 flex items-center gap-3 flex-wrap">
                      <span>{n.date}</span>
                      {n.tags?.map((t) => (
                        <span
                          key={t}
                          className="text-[10.5px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 bg-green-light text-green-dark"
                        >
                          {t}
                        </span>
                      ))}
                      {n.file && (
                        <a
                          href={mediaUrl(n.file)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-green font-semibold no-underline hover:underline"
                        >
                          📎 View PDF
                        </a>
                      )}
                    </p>
                  </div>
                </div>
              ))}
        </div>
      </section>
    </div>
  );
}
